import { decodeJwt } from '../../../decodeJwt';
import type { ParsedElevatedAccessToken } from '../elevatedAccessTokens.types';
import { normalizeScopes } from '../normalizeScopes';

export type ParseElevatedAccessTokenOptions = {
  /** JWT string containing the elevated access token. */
  token: string;
};

/**
 * Validates and parses an elevated access token from a JWT string.
 * Extracts scopes, expiration, and validates the token structure.
 *
 * Policy decisions:
 * - Tokens without scopes are rejected (returns null)
 * - Tokens with empty scopes array are rejected (returns null)
 * - Tokens without exp field are rejected (returns null)
 * - Expired tokens are rejected (returns null)
 *
 * @returns Parsed token data or null if token is invalid/expired
 * @notInstrumented
 */
export const parseElevatedAccessToken = ({
  token,
}: ParseElevatedAccessTokenOptions): ParsedElevatedAccessToken | null => {
  const payload = decodeJwt(token);
  const rawScopeClaim = payload?.scope;
  const processedScopes = rawScopeClaim?.split(' ') ?? [];
  const filteredScopes = processedScopes.filter((s) => s.length > 0);
  const singleUse = payload.singleUse ?? false;

  // Reject tokens without scopes or with empty scopes
  if (!filteredScopes || filteredScopes.length === 0) {
    return null;
  }

  // Reject tokens without exp field
  if (!payload.exp) {
    return null;
  }

  // Convert exp (seconds) to Date (milliseconds)
  const expiresAt = new Date(payload.exp * 1000);

  // Reject expired tokens
  if (expiresAt <= new Date()) {
    return null;
  }

  return {
    expiresAt,
    normalizedScopes: normalizeScopes(filteredScopes),
    scopes: filteredScopes,
    singleUse,
    token,
  };
};
